import { createFileRoute, notFound } from '@tanstack/react-router';

import { useBesluitId } from '@/api/queries/besluit';
import { useHoofdstukId } from '@/api/queries/objects';
import { useOverwegingByHoofdstukLineageId } from '@/api/queries/overwegingHoofdstuk';

import { BesluitChapterNavigation } from '../-components/BesluitChapterNavigation';
import { HTMLContent } from '../-components/HTMLContent';
import { useFindHoofdstukInGrondslagen } from './-utils/useFindHoofdstukInGrondslagen';
import { useViewerBesluitGrondslagen } from './-utils/useViewerBesluitGrondslagen';

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/besluit/$besluitId/_layout/inhoudelijke-overwegingen/$hoofdstukId'
)({
  loader: async ({
    params: { besluitId, hoofdstukId },
    context: { queryClient },
  }) => {
    const hoofdstuk = await queryClient.ensureQueryData(
      useHoofdstukId.getOptions(hoofdstukId)
    );

    if (!hoofdstuk) throw notFound();

    await Promise.all([
      queryClient.ensureQueryData(useBesluitId.getOptions(besluitId)),
      queryClient.ensureQueryData(
        useOverwegingByHoofdstukLineageId.queryOptions({
          hoofdstukLineageId: hoofdstuk.Lineage,
          besluitId,
        })
      ),
      queryClient.ensureQueryData(
        useViewerBesluitGrondslagen.getOptions({ besluitId })
      ),
    ]);
  },
  component: InhoudelijkeOverwegingen,
});

/**
 * Route component
 */
function InhoudelijkeOverwegingen() {
  const { besluitId, hoofdstukId } = Route.useParams();

  const besluit = useBesluitId(besluitId, { staleTime: Infinity });

  const { data: { grondslagen = [], lineageIdsWithVoorschriften } = {} } =
    useViewerBesluitGrondslagen({
      besluitId,
    });

  const { previousHoofdstuk, nextHoofdstuk, hoofdstuk } =
    useFindHoofdstukInGrondslagen({
      grondslagen,
      hoofdstukId,
    });

  const overweging = useOverwegingByHoofdstukLineageId(
    { hoofdstukLineageId: hoofdstuk?.Lineage ?? '', besluitId },
    { enabled: !!hoofdstuk }
  );

  if (
    !besluit.data ||
    overweging.isLoading ||
    !hoofdstuk ||
    !lineageIdsWithVoorschriften
  )
    return null;

  return (
    <>
      <HTMLContent
        besluit={besluit.data}
        title={<span dangerouslySetInnerHTML={{ __html: hoofdstuk.Titel }} />}
        content={overweging.data?.InhoudelijkeOverweging}
      />

      <BesluitChapterNavigation
        previousHoofdstuk={previousHoofdstuk}
        nextHoofdstuk={nextHoofdstuk}
        lineageIdsWithVoorschriften={lineageIdsWithVoorschriften}
      />
    </>
  );
}
